import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Fetch } from '../../utils/Utils.js';
import DataCollection from '../../shared/DataCollection';
import WeightLotCardList from './WeightLotCardList.js';
import '../../shared/DefaultBadge.css';
import './WeightLots.css';

const formatLotCode = (lotId, prefix) =>
  lotId != null ? `${prefix}-${String(lotId).padStart(4, '0')}` : '-';

const formatScoreDatetime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  const HH = String(d.getHours()).padStart(2, '0');
  const MM = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yy} ${HH}:${MM}`;
};

// ── Main list component ──────────────────────────────────────────────────────
export const ListWeightLots = ({ refreshKey, onDefaultSet }) => {
  const { t }    = useTranslation();
  const navigate = useNavigate();
  const [defaultLotId,    setDefaultLotId]    = useState(null);
  const [animalCounts,    setAnimalCounts]    = useState({});
  const [latestScoreMap,  setLatestScoreMap]  = useState({});
  const [protocols,       setProtocols]       = useState([]);

  useEffect(() => {
    Fetch('api/data/lots.plans', 'GET', null,
      (data) => setProtocols(data || []),
      () => {}
    );
    Fetch('api/data/lots.animals?search_fields=type&search_values=weight', 'GET', null,
      (data) => {
        const counts = {};
        (data || []).forEach(row => {
          counts[row.lot_id] = (counts[row.lot_id] || 0) + 1;
        });
        setAnimalCounts(counts);
      },
      () => {}
    );
    Fetch('api/data/weight.lots.scores', 'GET', null,
      (data) => {
        const map = {};
        (data || []).forEach(row => {
          const existing = map[row.lot_id];
          if (!existing || row.scored_at > existing.scored_at) {
            map[row.lot_id] = row;
          }
        });
        setLatestScoreMap(map);
      },
      () => {}
    );
  }, [refreshKey]);

  useEffect(() => {
    Fetch('api/ranch', 'GET', null,
      (data) => { if (data?.default_weight_lot) setDefaultLotId(data.default_weight_lot); },
      () => {}
    );
  }, []);

  const getProtocolName = (protocolId) => {
    if (!protocolId) return '—';
    const p = protocols.find(p => p._id === protocolId || p._id?.$oid === protocolId);
    return p ? p.name : '—';
  };

  const handleSetDefault = (lot) => {
    Fetch('api/ranch', 'PATCH', JSON.stringify({ default_weight_lot: lot.lot_id }),
      () => {
        setDefaultLotId(lot.lot_id);
        onDefaultSet?.(lot);
      },
      () => {}
    );
  };

  const columns = [
    {
      name: t('weight_lot_code'),
      selector: row => row.lot_id,
      sortable: true,
      cell: row => (
        <span className="wl-lot-link" onClick={() => navigate(`/weight/lots/${row.lot_id}`)}>
          {formatLotCode(row.lot_id, t('weight_lot_prefix'))}
        </span>
      ),
    },
    {
      name: t('weight_lot_name'),
      selector: row => row.name || '-',
      sortable: true,
    },
    {
      name: t('animals_count'),
      selector: row => animalCounts[row.lot_id] || 0,
      sortable: true,
    },
    {
      name: t('plan_name'),
      selector: row => getProtocolName(row.protocol_id),
      sortable: true,
    },
    ...(Object.keys(latestScoreMap).length > 0 ? [
      {
        name: t('last_score_short'),
        selector: row => latestScoreMap[row.lot_id]?.scored_at || '',
        sortable: true,
        cell: row => {
          const s = latestScoreMap[row.lot_id];
          return s ? formatScoreDatetime(s.scored_at) : '—';
        },
      },
      {
        name: t('score_value'),
        selector: row => latestScoreMap[row.lot_id]?.score ?? null,
        sortable: true,
        cell: row => {
          const s = latestScoreMap[row.lot_id];
          if (!s) return '—';
          const v = s.score;
          const color = v > 0 ? '#2d7a2d' : v < 0 ? '#c0392b' : '#888';
          const label = v > 0 ? `+${v}` : String(v);
          return <span style={{ color, fontWeight: 600 }}>{label}</span>;
        },
      },
    ] : []),
    {
      name: t('default'),
      width: '150px',
      cell: row => row.lot_id === defaultLotId
        ? <span className="default-badge">★ {t('default')}</span>
        : <button className="set-default-btn" onClick={() => handleSetDefault(row)}>{t('set_as_default')}</button>,
    },
  ];

  return (
    <DataCollection
      collection="lots"
      searchFields={{ type: 'weight' }}
      columns={columns}
      sortField={2}
      refreshKey={refreshKey}
      mobileComponent={(data, onRefresh) => (
        <WeightLotCardList
          data={data}
          onRefresh={onRefresh}
          defaultLotId={defaultLotId}
          onSetDefault={handleSetDefault}
          animalCounts={animalCounts}
          latestScoreMap={latestScoreMap}
          getProtocolName={getProtocolName}
        />
      )}
    />
  );
};

export default ListWeightLots;
